// The e15 station snapshot as an OFFLINE-TOLERANT read (spec 04 §5 row 15,
// slice 04-B-3). One hook, the `useCachedRead` pattern: a cached snapshot
// renders IMMEDIATELY with its taken-at time — never a spinner forever on a
// plant AP with no backhaul — and the live answer replaces it when it lands.
// The snapshot is stored VERBATIM (floor-read-cache), so the D-072 served
// order survives the cache byte-for-byte and the screens can obey the
// never-re-sort rule against either source.

import { useQueryClient } from "@tanstack/react-query";
import { client } from "../../../lib/api";
import { refreshQuery } from "../../../lib/query";
import { unwrap } from "../../../lib/request";
import { readCacheKey } from "../lib/floor-read-cache";
import type { StationSnapshot } from "../lib/production-floor";
import { useCachedRead, floorReadSignal, type FloorRead } from "./use-cached-read";

export const stationSnapshotQueryKey = (workCenterId: string) =>
  ["floor", "station-snapshot", workCenterId] as const;

export const stationSnapshotCacheKey = (workCenterId: string): string =>
  readCacheKey("station", workCenterId);

export function useStationSnapshot(workCenterId: string): FloorRead<StationSnapshot> {
  return useCachedRead<StationSnapshot>({
    queryKey: stationSnapshotQueryKey(workCenterId),
    cacheKey: stationSnapshotCacheKey(workCenterId),
    enabled: workCenterId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/floor/stations/{workCenterId}/snapshot", {
          params: { path: { workCenterId } },
          signal: floorReadSignal(signal),
        }),
        "station-snapshot",
      ),
  });
}

/** Refresh the snapshot after a tap the server confirmed APPLIED — the queue
 * card's status/qty moved server-side. Queued/conflicted taps refresh nothing:
 * the server state did not change (queued) or changed into the conflict queue
 * (reviewed in the office, not here).
 *
 * CANCEL BEFORE INVALIDATE, and both are load-bearing. react-query's
 * invalidate only cancels an in-flight fetch when the query ALREADY HAS data
 * (`query.fetch`: `state.data !== undefined && cancelRefetch`); on a
 * first-ever read — a freshly opened op screen, which is exactly when a worker
 * taps Start — it coalesces into the in-flight fetch instead, and a snapshot
 * read BEFORE the event applied lands as the "fresh" answer with no follow-up
 * request. The screen then says Ready after the server said applied, until
 * the worker re-taps into the conflict tray (M04-S10, staging 2026-08-05).
 * cancelQueries cancels unconditionally — reaching the socket through the
 * signal the queryFn now threads — and the invalidate that follows always
 * refetches. That pairing is `refreshQuery` (lib/query.ts), shared with the
 * office call sites that hit the same window. Pinned in
 * use-cached-read.race.test.tsx. */
export function useStationSnapshotRefresh(workCenterId: string): () => void {
  const qc = useQueryClient();
  return () => {
    void refreshQuery(qc, stationSnapshotQueryKey(workCenterId));
  };
}
